/**
 * SA-1037 (spec §5.6): a step is SKIPPED on a record when it has no visit on
 * the record's path, is not the current step, and some path entry's exit
 * targets a step such that, in reading order, the entry's step comes before
 * it and the target after it — the `reused` exit takes step 5 straight to 7,
 * over 6. The latest such entry is its reason. A back-edge never skips; a
 * revision or a re-open is not an exit, so it skips nothing; a step the record
 * reaches later is simply visited. Pure, over the ledger path the server serves.
 */
import type { ProcessOverviewVM } from '@/utils/workflowOverview'

export interface StepSkip { fromStep: string; exit: string; targetLabel: string }

export function skippedSteps(
  vm: ProcessOverviewVM,
  path: readonly { step: string; exit: string | null }[] | undefined,
  currentStep: string | null,
): Map<string, StepSkip> {
  const skips = new Map<string, StepSkip>()
  if (!path?.length) return skips
  const index = new Map(vm.steps.map((s, i) => [s.id, i]))
  const visited = new Set(path.map((p) => p.step))
  for (const entry of path) {
    if (!entry.exit) continue
    const from = index.get(entry.step)
    const step = from === undefined ? undefined : vm.steps[from]
    const exit = step?.exits.find((e) => e.name === entry.exit && e.targetKind === 'step')
    const to = exit ? index.get(exit.target) : undefined
    if (from === undefined || !exit || to === undefined || to <= from + 1) continue
    for (let k = from + 1; k < to; k += 1) {
      const id = vm.steps[k].id
      if (!visited.has(id) && id !== currentStep) skips.set(id, { fromStep: entry.step, exit: exit.name, targetLabel: exit.targetLabel })
    }
  }
  return skips
}
